import * as React from 'react'
import {
  findTypeaheadValue,
  firstEnabledValue,
  lastEnabledValue,
  stepEnabledValue,
  type SelectOption,
} from './select-option-utils'

/** Letters typed within this window build one type-ahead search ("mi" → "Milano"). */
const TYPEAHEAD_RESET_MS = 700

interface SelectKeyboardNavigationParams {
  isOpen: boolean
  /** Every option, flat (closed type-ahead picks from all of them). */
  allOptions: SelectOption[]
  /** Options currently shown in the open list (after search), flat. */
  visibleOptions: SelectOption[]
  value?: string
  activeValue: string | null
  /** Moves the highlighted row; keyboard moves ask for it to be scrolled into view. */
  setActiveValue: (value: string | null, options?: { scroll?: boolean }) => void
  openMenu: () => void
  /** Closes the list; `refocus` hands focus back to the trigger. */
  closeMenu: (refocus: boolean) => void
  /** Picks an option from the open list (closes and refocuses the trigger). */
  selectValue: (value: string) => void
  /** Changes the value without opening (closed-trigger type-ahead, like a native select). */
  changeValue: (value: string) => void
}

const isPrintableKey = (event: React.KeyboardEvent) =>
  event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey

/**
 * WAI-ARIA combobox/listbox keyboard behaviour for `Select`.
 *
 * - Closed trigger: ArrowDown/ArrowUp/Enter open; Space opens unless a type-ahead is
 *   in progress (then it is part of the search); printable keys type-ahead the value.
 * - Open list (focus on the trigger, or in the search box when searchable):
 *   ArrowUp/Down/Home/End move the highlighted row, skipping disabled ones; Enter picks it.
 *   Without a search box, letters type-ahead the highlight and Space picks it.
 * - Esc closes and refocuses the trigger; Tab closes and refocuses the trigger first so
 *   the browser's own Tab move starts from there.
 */
export function useSelectKeyboardNavigation({
  isOpen,
  allOptions,
  visibleOptions,
  value,
  activeValue,
  setActiveValue,
  openMenu,
  closeMenu,
  selectValue,
  changeValue,
}: SelectKeyboardNavigationParams) {
  const typeaheadRef = React.useRef({ buffer: '', at: 0 })

  const isTypeaheadLive = () => {
    const { buffer, at } = typeaheadRef.current
    return buffer !== '' && Date.now() - at < TYPEAHEAD_RESET_MS
  }

  /** Adds `key` to the type-ahead buffer; repeating a single letter cycles instead of extending. */
  const pushTypeahead = (key: string) => {
    const live = isTypeaheadLive()
    const { buffer } = typeaheadRef.current
    const lower = key.toLowerCase()
    const repeat = live && buffer.length === 1 && buffer === lower
    const next = repeat ? buffer : live ? buffer + lower : lower
    typeaheadRef.current = { buffer: next, at: Date.now() }
    return next
  }

  /** Keys that move or pick the highlighted row; shared by the trigger and the search box. */
  const handleListNavigationKey = (event: React.KeyboardEvent): boolean => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setActiveValue(stepEnabledValue(visibleOptions, activeValue, 1), { scroll: true })
        return true
      case 'ArrowUp':
        event.preventDefault()
        setActiveValue(stepEnabledValue(visibleOptions, activeValue, -1), { scroll: true })
        return true
      case 'Home':
        event.preventDefault()
        setActiveValue(firstEnabledValue(visibleOptions), { scroll: true })
        return true
      case 'End':
        event.preventDefault()
        setActiveValue(lastEnabledValue(visibleOptions), { scroll: true })
        return true
      case 'Enter': {
        event.preventDefault()
        const active = visibleOptions.find((o) => o.value === activeValue)
        if (active && !active.disabled) selectValue(active.value)
        return true
      }
      case 'Escape':
        event.preventDefault()
        // Keep an enclosing modal/drawer from also closing on the same Esc.
        event.stopPropagation()
        closeMenu(true)
        return true
      case 'Tab':
        // No preventDefault: refocus the trigger, then let the browser move on from it.
        closeMenu(true)
        return true
      default:
        return false
    }
  }

  const onTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.nativeEvent.isComposing) return
    if (!isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') {
        event.preventDefault()
        openMenu()
        return
      }
      if (event.key === ' ' && !isTypeaheadLive()) {
        event.preventDefault()
        openMenu()
        return
      }
      if (isPrintableKey(event)) {
        event.preventDefault()
        const match = findTypeaheadValue(allOptions, pushTypeahead(event.key), value ?? null)
        if (match !== null && match !== value) changeValue(match)
      }
      return
    }

    // Open without a search box: the trigger keeps focus and drives the list.
    if (handleListNavigationKey(event)) return
    if (event.key === ' ' && !isTypeaheadLive()) {
      event.preventDefault()
      const active = visibleOptions.find((o) => o.value === activeValue)
      if (active && !active.disabled) selectValue(active.value)
      return
    }
    if (isPrintableKey(event)) {
      event.preventDefault()
      const match = findTypeaheadValue(visibleOptions, pushTypeahead(event.key), activeValue)
      if (match !== null) setActiveValue(match, { scroll: true })
    }
  }

  /** Search box keys: typing (incl. Space) stays in the box; only navigation keys are taken. */
  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // An IME (e.g. Vietnamese Telex) uses Enter/arrows to finish a word: leave those to it.
    if (event.nativeEvent.isComposing) return
    handleListNavigationKey(event)
  }

  return { onTriggerKeyDown, onSearchKeyDown }
}
